import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { del } from "@/utils/api-handler";
import { toast } from "sonner";
import { DeleteConfirmationDialog } from "@/components/shared/delete-dialog";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Trash2, Download } from "lucide-react";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import {
    buildBlobDownloadHref,
    sanitizeDownloadFilename,
} from "@/utils/download-filename";

export interface CommandTeamFile {
    id: string;
    file_name: string;
    file_url?: string;
    file_size?: number;
    mime_type?: string;
    status?: string;
    source?: string;
    session_id?: string;
    session_title?: string;
    agent_id?: string;
    created_at: string;
    updated_at: string;
}

export function getExtensionFromMime(mime: string | undefined): string {
    if (!mime) return "bin";
    const subtype = mime.split("/").pop()?.toLowerCase().replace(/^x-/, "") ?? "";
    if (subtype === "plain") return "txt";
    if (subtype === "jpeg") return "jpg";
    if (subtype === "msword") return "doc";
    if (subtype === "ms-excel" || subtype.endsWith("ms-excel")) return "xls";
    if (subtype === "ms-powerpoint" || subtype.endsWith("ms-powerpoint")) return "ppt";
    if (subtype.includes("octet-stream")) return "bin";
    if (subtype.startsWith("vnd.")) {
        if (subtype.includes("wordprocessing") || (subtype.includes("document") && subtype.includes("word"))) return "docx";
        if (subtype.includes("spreadsheet") || subtype.includes("sheet")) return "xlsx";
        if (subtype.includes("presentation")) return "pptx";
        // Never expose MIME tails like "document" as the download extension
        if (subtype.endsWith(".document")) return "docx";
        return subtype.split(".").pop()?.slice(0, 6) || "bin";
    }
    const base = subtype.includes("+") ? subtype.split("+")[0] : subtype;
    return base.slice(0, 6) || "bin";
}

function proxyUrl(url: string, filename?: string): string {
    return buildBlobDownloadHref(url, filename);
}

function getDownloadFilename(file: Pick<CommandTeamFile, "file_name" | "file_url" | "mime_type">): string {
    const name = sanitizeDownloadFilename(file.file_name?.trim() || file.file_url || "file");
    const hasExt = /\.[a-z0-9]{1,10}$/i.test(name);
    if (hasExt) return name;
    const ext = getExtensionFromMime(file.mime_type);
    const base = name.replace(/\.[^/.]+$/, "") || "file";
    return sanitizeDownloadFilename(`${base}.${ext}`);
}

export function downloadCommandTeamFile(file: CommandTeamFile): void {
    const url = file.file_url;
    if (!url) return;
    const filename = getDownloadFilename(file);
    const a = document.createElement("a");
    a.href = proxyUrl(url, filename);
    a.download = filename;
    a.click();
}

export async function openFileInNewTab(file: { file_url: string; mime_type?: string; file_name?: string }): Promise<void> {
    const { file_url } = file;
    try {
        const filename = getDownloadFilename({
            file_name: file.file_name || "file",
            file_url,
            mime_type: file.mime_type,
        });
        window.open(proxyUrl(file_url, filename), "_blank", "noopener");
    } catch {
        window.open(file_url, "_blank");
    }
}

interface ActionCellProps {
    row: { original: CommandTeamFile };
}

export function CommandTeamFileManagerAction({ row }: ActionCellProps) {
    const queryClient = useQueryClient();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const deleteMutation = useProtectedMutation({
        mutationFn: () => del(`/api/command-team/files/${row.original.id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["command-team-files"] });
            toast.success("File deleted successfully", {
                description: "The file has been removed.",
            });
            setDeleteDialogOpen(false);
        },
        onError: (error: any) => {
            toast.error("Failed to delete file", {
                description:
                    error.response?.data?.message ||
                    "An error occurred while deleting the file.",
            });
            setDeleteDialogOpen(false);
        },
    });

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync();
        } catch {
            // Error handled in onError
        }
    };

    const displayName = row.original.file_name || "File";

    const handleDownload = () => {
        downloadCommandTeamFile(row.original);
    };

    return (
        <div className="flex items-center justify-end">
            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleDelete}
                isLoading={deleteMutation.isPending}
                itemName={displayName}
                itemType="file"
            />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-[150px] py-2"
                    onCloseAutoFocus={(e) => e.preventDefault()}
                >
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {row.original.file_url && (
                        <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={handleDownload}
                        >
                            <Download className="mr-2 h-4 w-4" />
                            Download
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        className="justify-start text-destructive cursor-pointer hover:text-destructive"
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={deleteMutation.isPending}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
